import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { OutputListEditorDescription_EditorType } from "@smartsuite/automation-engine-api";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import {
  DropdownPanel,
  MenuOption,
  MenuOptionWrapper,
  PickerInputField,
  Text,
} from "@smartsuite/react-ui/lib";
import type { PillPickerEditorCommands } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { PickerStyleWrapper } from "../../../../../../../../components/PickerStyleWrapper";
import { useTheme } from "../../../../../../../../stores";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type {
  DynamicSection,
  DynamicSectionOption,
  DynamicSectionOptionGroup,
} from "../../../../../../../../types/line";
import { useConverters } from "../../../../../../../../utils/conditions-coverter.hook";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { convertTriggerToDynamicSection } from "../../../../utils";
import { SubMenuOptions } from "../../containers/SubMenuOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import { DateTab } from "../DateEditor";
import { generalTypes } from "../SelectEditor/select-output-types";
import { calculateConfigValue } from "../utils";
import { StyledTitle } from "./styles";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { recordOutputIds } from "../../../../../../../AutomationActivity/ActivityDetails/components/MergeRecordsGroup/MergeRecordsGroup";

export const FieldsEditor: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  integrations: IntegrationDescription[];
  description: InputDescription;
  onClickTrash?: () => void;
  action?: ActionCandidate;
}> = ({
  input,
  onChange,
  trigger,
  integrations,
  description,
  onClickTrash,
  action,
}) => {
  const theme = useTheme();
  const buttonRef = useRef();
  const commandsRef = useRef<PillPickerEditorCommands>();
  const [isOpen, setIsOpen] = useState(false);
  const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
  const [pills, setPills] = useState<PillNodeAttrs[]>([]);
  const [tab, setTab] = useState<"root" | "trigger-fields" | "sub-menu">(
    "root",
  );
  const store = useRootStore();
  const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
    useFieldMissingValidation();
  const [subOptions, setSubOptions] = useState<DynamicSectionOptionGroup>({
    options: [],
    label: "",
  });
  const { candidate } = store.automations;
  const needToShowError = (hash: string): boolean => {
    return candidate.isActionSubmitted(hash) && pills.length === 0;
  };

  const { valueConfigToTargetPills } = useConverters();

  const addPill = (attrs: PillNodeAttrs): void => {
    const newPills = [...pills, attrs];
    setPills(newPills);
    setIsOpen(false);
    onChange({
      ...input,
      value: calculateConfigValue(
        newPills,
        [],
        trigger,
        isMergeType(description),
      ),
    });
  };

  const removePill = (_: number, pill: FieldTypePillProps): void => {
    const newPills = pills.filter((item) => item !== pill);
    setPills(newPills);
    onChange({
      ...input,
      value: calculateConfigValue(
        newPills,
        [],
        trigger,
        isMergeType(description),
      ),
    });
    if (isMergeType(description)) {
      setTab("trigger-fields");
    }
  };

  useEffect(() => {
    if (isMergeType(description)) {
      setTab("trigger-fields");
      const dynamicSection = convertTriggerToDynamicSection(
        trigger,
        integrations,
        generalTypes,
        generalTypes,
        [
          "addressfield",
          "emailfield",
          "phonefield",
          "textfield",
          "textareafield",
          "singleselectfield",
          "multipleselectfield",
          "statusfield",
          "recordtitlefield",
          "linkedrecordfield",
        ],
        [],
        [
          { sourceType: "formulafield", types: [] },
          { sourceType: "lookupfield", types: [] },
        ],
      );
      // filter not allowed fields
      const filteredOptions = dynamicSection?.options.filter(
        (option) => !recordOutputIds.includes(option.outputId),
      );
      if (dynamicSection !== null) {
        dynamicSection.options = filteredOptions ?? [];
      }

      setTriggerMenu(dynamicSection);
    } else {
      const dynamicSection = convertTriggerToDynamicSection(
        trigger,
        integrations,
        undefined,
        undefined,
        undefined,
        [],
      );
      if (dynamicSection !== null) {
        dynamicSection.options = dynamicSection.options.filter(
          (option) =>
            !option.outputId.includes(".phone") &&
            !option.outputId.includes(".email") &&
            !option.outputId.includes(".on") &&
            !option.outputId.includes(".by") &&
            option.outputId !== "field-followed_by" &&
            option.outputId !== "field-comments_count",
        );
        setTriggerMenu(dynamicSection);
      }
    }
  }, [integrations, trigger]);

  useEffect(() => {
    if (R.isNil(input.value)) return;

    setPills(
      valueConfigToTargetPills(
        input.value,
        trigger,
        [],
        store.automations.candidate.getTimezone(),
        (label) => label,
      ),
    );
  }, [input.value, trigger]);

  if (trigger.description.recordListOutput === undefined) {
    return null;
  }

  const goSubMenu = (options: DynamicSectionOption[], label: string): void => {
    setSubOptions({ options, label });
    setTab(DateTab["sub-menu"]);
  };

  const buildMenu = (): React.ReactNode => {
    return (
      <DropdownPanel
        targetElementRef={buttonRef}
        isActive={isOpen}
        at={POSITION_AT}
        my={POSITION_MY}
        minWidth={300}
        arrowBackground="white"
        portalStyles={getLocalCssColorVariables(theme)}
        onClickOutside={() => setIsOpen(false)}
      >
        {tab === "root" && (
          <MenuOptionWrapper>
            <MenuOption
              submenuArrow="right"
              onClick={() => setTab("trigger-fields")}
            >
              {t("field-popups.trigger-popup")}
            </MenuOption>
          </MenuOptionWrapper>
        )}
        {tab === "trigger-fields" && (
          <TriggerOptions
            disableTitle={isMergeType(description)}
            searchPlaceholder={t("automation-form.search-fields")}
            onNavigateBack={() => setTab("trigger-fields")}
            addPill={addPill}
            options={triggerMenu?.options ?? []}
            disabledOptionId={pills.map((pill) => pill.title)}
            goSubMenu={goSubMenu}
          />
        )}
        {tab === "sub-menu" && (
          <SubMenuOptions
            title={subOptions.label}
            hasSingleMenuItem={triggerMenu?.options.length === 0}
            onNavigateBack={() => setTab(DateTab["trigger-fields"])}
            addPill={(pill) => {
              setTab("trigger-fields");
              addPill(pill);
            }}
            prevTab={Tab.ROOT}
            options={subOptions.options}
            disabledOptionId={pills.map((pill) => pill.title)}
          />
        )}
      </DropdownPanel>
    );
  };

  return (
    <PickerStyleWrapper>
      <CaptionBlock description={description} onClickTrash={onClickTrash} />
      <StyledTitle>
        <Text weight="medium">{t("caption.Fields")}</Text>
      </StyledTitle>
      <PickerInputField
        className={needToShowError(action?.hash ?? "") ? "is-error" : ""}
        state={getEditorState(isOpen, !isInputValidByFieldMissing(input))}
        buttonRef={buttonRef}
        // @ts-expect-error
        commandsRef={commandsRef}
        isOpen={isOpen}
        pills={pills}
        pillStyle={{
          textSize: "m",
        }}
        onButtonClick={() => setIsOpen((open) => !open)}
        onPillRemove={removePill}
        placeholder={
          isOpen ? t("automation-form.select-field-placeholder") : ""
        }
      >
        {buildMenu()}
      </PickerInputField>
      <FieldMissingError message={getFieldMissingErrorMessage(input)} />
    </PickerStyleWrapper>
  );
};

const getEditorState = (
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (isOpen) {
    return "active";
  } else {
    return "default";
  }
};

const isMergeType = (description: InputDescription): boolean => {
  return (
    description.editor?.editor.oneofKind === "outputs" &&
    description.editor.editor.outputs.type ===
      OutputListEditorDescription_EditorType.MERGE
  );
};
